import React, { useState, useEffect, useCallback } from "react";
import { X, Upload, Trash2, Image as ImageIcon } from "lucide-react";
import { getImageUrl } from "../utils/imageHelper";
import { getImageUploadSettings } from "../services/settingsApi";

// Default fallback configuration
const DEFAULT_IMAGE_CONFIG = {
  width: 600,
  height: 600,
  maxBytes: 1024 * 1024, // 1MB
};

/**
 * Resize image to specified dimensions and return File + preview data URL
 */
const resizeImage = (file, { width, height, maxBytes }) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");

        // Calculate scale to cover the canvas (crop to fit)
        const scale = Math.max(width / img.width, height / img.height);
        const dx = (width - img.width * scale) / 2;
        const dy = (height - img.height * scale) / 2;

        // Fill with white background
        ctx.fillStyle = "#fff";
        ctx.fillRect(0, 0, width, height);
        
        // Draw image centered and scaled
        ctx.drawImage(img, dx, dy, img.width * scale, img.height * scale);

        canvas.toBlob(
          (blob) => {
            if (!blob) return reject(new Error("Could not process image"));
            if (blob.size > maxBytes) {
              return reject(new Error(`Image must be under ${(maxBytes / 1024).toFixed(0)}KB`));
            }
            const resizedFile = new File([blob], file.name, { 
              type: blob.type || file.type || "image/jpeg" 
            });
            resolve({ file: resizedFile, preview: canvas.toDataURL(blob.type || "image/jpeg") });
          },
          file.type || "image/jpeg",
          0.92
        );
      };
      img.onerror = () => reject(new Error("Unable to read image"));
      img.src = reader.result;
    };
    reader.onerror = () => reject(new Error("Unable to read file"));
    reader.readAsDataURL(file);
  });

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const INDIAN_STATES = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
];

const StoreForm = ({ store, onSubmit, onCancel, isLoading }) => {
  const [formData, setFormData] = useState({
    branch_code: "",
    store_name: "",
    phone_number: "",
    manager_number: "",
    email: "",
    website: "",
    google_map_link: "",
    store_description: "",
    address: "",
    city: "",
    state: "",
    lat: "",
    long: "",
    is_active: true,
  });

  const [openingHours, setOpeningHours] = useState({});
  const [errors, setErrors] = useState({});
  const [stateOptions, setStateOptions] = useState(INDIAN_STATES);
  
  // Image upload state
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [imageConfig, setImageConfig] = useState(DEFAULT_IMAGE_CONFIG);
  const [imageError, setImageError] = useState("");

  // Fetch image upload settings on mount
  useEffect(() => {
    const fetchImageSettings = async () => {
      try {
        const response = await getImageUploadSettings();
        const settings = response.data.data;
        setImageConfig({
          width: settings.image_default_width,
          height: settings.image_default_height,
          maxBytes: settings.image_max_size_bytes,
        });
      } catch (err) {
        console.warn("Failed to fetch image upload settings, using defaults:", err);
        setImageConfig(DEFAULT_IMAGE_CONFIG);
      }
    };
    fetchImageSettings();
  }, []);

  useEffect(() => {
    if (store) {
      setFormData({
        branch_code: store.branch_code || "",
        store_name: store.store_name || "",
        phone_number: store.phone_number || "",
        manager_number: store.manager_number || "",
        email: store.email || "",
        website: store.website || "",
        google_map_link: store.google_map_link || "",
        store_description: store.store_description || "",
        address: store.address || "",
        city: store.city || "",
        state: store.state || "",
        lat: store.lat !== null && store.lat !== undefined ? String(store.lat) : "",
        long: store.long !== null && store.long !== undefined ? String(store.long) : "",
        is_active: store.is_active !== undefined ? store.is_active : true,
      });

      // Parse opening hours
      if (store.opening_hours && typeof store.opening_hours === "object") {
        setOpeningHours(store.opening_hours);
      }

      if (store.state && !INDIAN_STATES.includes(store.state)) {
        setStateOptions([...INDIAN_STATES, store.state]);
      }
      
      // Reset image state when store changes
      setSelectedFile(null);
      setImagePreview(null);
      setRemoveImage(false);
    } else {
      // Initialize default opening hours for new store
      const defaultHours = {};
      DAYS.forEach((day) => {
        defaultHours[day] = { open: "09:00", close: "21:00", closed: false };
      });
      setOpeningHours(defaultHours);
      setStateOptions(INDIAN_STATES);
    }
  }, [store]);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const newErrors = { ...prev };
      delete newErrors[name];
      return newErrors;
    });
  }, []);

  const handleHourChange = useCallback((day, field, value) => {
    setOpeningHours((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: value,
      },
    }));
  }, []);

  const handleClosedToggle = useCallback((day) => {
    setOpeningHours((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        closed: !prev[day]?.closed,
      },
    }));
  }, []);

  // Handle image file selection with dynamic settings
  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      setImageError("Please select an image file (JPEG, PNG, or WebP)");
      return;
    }

    try {
      setImageError("");
      
      // Resize image to configured dimensions
      const { file: resizedFile, preview } = await resizeImage(file, imageConfig);
      
      setSelectedFile(resizedFile);
      setImagePreview(preview);
      setRemoveImage(false);
      
      console.log(`✅ Image resized to ${imageConfig.width}×${imageConfig.height}, size: ${(resizedFile.size / 1024).toFixed(1)}KB`);
    } catch (err) {
      console.error("Image processing error:", err);
      setSelectedFile(null);
      setImagePreview(null);
      setImageError(err.message || `Image must be ${imageConfig.width}×${imageConfig.height} and under ${(imageConfig.maxBytes / 1024).toFixed(0)}KB`);
    }
  };

  // Handle image removal
  const handleRemoveImage = () => {
    setSelectedFile(null);
    setImagePreview(null);
    setRemoveImage(true); // Flag for deletion on save
    setShowDeleteConfirm(false);
  };

  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.store_name.trim()) {
      newErrors.store_name = "Store name is required";
    }
    if (!formData.address.trim()) {
      newErrors.address = "Address is required";
    }
    if (!formData.city.trim()) {
      newErrors.city = "City is required";
    }
    if (!formData.state.trim()) {
      newErrors.state = "State is required";
    }
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }
    
    // Validate lat/long if provided
    if (formData.lat && (isNaN(formData.lat) || parseFloat(formData.lat) < -90 || parseFloat(formData.lat) > 90)) {
      newErrors.lat = "Latitude must be between -90 and 90";
    }
    if (formData.long && (isNaN(formData.long) || parseFloat(formData.long) < -180 || parseFloat(formData.long) > 180)) {
      newErrors.long = "Longitude must be between -180 and 180";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      if (!validateForm()) {
        return;
      }

      try {
        // Create FormData for multipart submission (supports both JSON and files)
        const formDataToSend = new FormData();

        // Add all form fields
        formDataToSend.append("branch_code", formData.branch_code || "");
        formDataToSend.append("store_name", formData.store_name);
        formDataToSend.append("phone_number", formData.phone_number || "");
        formDataToSend.append("manager_number", formData.manager_number || "");
        formDataToSend.append("email", formData.email || "");
        formDataToSend.append("website", formData.website || "");
        formDataToSend.append("google_map_link", formData.google_map_link || "");
        formDataToSend.append("store_description", formData.store_description || "");
        formDataToSend.append("address", formData.address);
        formDataToSend.append("city", formData.city);
        formDataToSend.append("state", formData.state);
        // Send empty string for empty lat/long - backend will convert to null (Prisma expects Float or null)
        formDataToSend.append("lat", formData.lat && formData.lat.trim() !== "" ? formData.lat : "");
        formDataToSend.append("long", formData.long && formData.long.trim() !== "" ? formData.long : "");
        formDataToSend.append("opening_hours", JSON.stringify(openingHours));
        formDataToSend.append("is_active", formData.is_active.toString());

        // Handle image upload or removal (only for editing existing stores)
        if (store && store.id) {
          if (selectedFile) {
            formDataToSend.append("image", selectedFile);
            console.log("✅ Image file attached:", {
              name: selectedFile.name,
              size: selectedFile.size,
              type: selectedFile.type,
            });
          } else if (removeImage) {
            formDataToSend.append("remove_image", "true");
            console.log("🗑️ Image marked for removal");
          }
        }

        // Submit the complete store data (including image if present)
        await onSubmit(formDataToSend);
      } catch (error) {
        console.error("Form submission error:", error);
        throw error; // Re-throw to let parent handle it
      }
    },
    [formData, openingHours, onSubmit, validateForm, store, selectedFile, removeImage],
  );

  return (
    <div className="bg-white rounded-xl shadow-lg max-w-3xl mx-auto flex flex-col" style={{ height: '85vh', maxHeight: '850px' }}>
      <div className="flex justify-between items-center p-4 pb-3 border-b border-gray-200">
        <h2 className="text-xl font-semibold text-[#6b4226]">
          {store ? "Edit Store" : "Add New Store"}
        </h2>
        <button
          onClick={onCancel}
          className="text-gray-500 hover:text-gray-700 transition-colors"
          type="button"
        >
          <X size={20} />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 text-sm">
        {/* Basic Information */}
        <div>
          <h3 className="text-base font-semibold text-gray-700 mb-2">Basic Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Branch Code
              </label>
              <input
                type="text"
                name="branch_code"
                value={formData.branch_code}
                onChange={handleInputChange}
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg focus:ring-1 focus:ring-[#6b4226] focus:border-transparent"
                placeholder="e.g., CNDLS01"
              />
              <p className="text-[10px] text-gray-500 mt-1">
                Unique identifier for this branch
              </p>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Store Status
              </label>
              <div className="flex items-center gap-4 h-[34px]">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="is_active"
                    checked={formData.is_active === true}
                    onChange={() => setFormData(prev => ({ ...prev, is_active: true }))}
                    className="w-4 h-4 text-green-600 border-gray-300 focus:ring-green-500"
                  />
                  <span className="text-sm text-gray-700">Active</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="is_active"
                    checked={formData.is_active === false}
                    onChange={() => setFormData(prev => ({ ...prev, is_active: false }))}
                    className="w-4 h-4 text-red-600 border-gray-300 focus:ring-red-500"
                  />
                  <span className="text-sm text-gray-700">Inactive</span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Store Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="store_name"
                value={formData.store_name}
                onChange={handleInputChange}
                className={`w-full px-2.5 py-1.5 border rounded-lg focus:ring-1 focus:ring-[#6b4226] focus:border-transparent ${
                  errors.store_name ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="The Coffee Bean & Tea Leaf"
              />
              {errors.store_name && (
                <p className="text-red-500 text-xs mt-1">{errors.store_name}</p>
              )}
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Phone Number</label>
              <input
                type="text"
                name="phone_number"
                value={formData.phone_number}
                onChange={handleInputChange}
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg focus:ring-1 focus:ring-[#6b4226] focus:border-transparent"
                placeholder="+91 123-456-7890"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Manager Number</label>
              <input
                type="text"
                name="manager_number"
                value={formData.manager_number}
                onChange={handleInputChange}
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg focus:ring-1 focus:ring-[#6b4226] focus:border-transparent"
                placeholder="+91 987-654-3210"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`w-full px-2.5 py-1.5 border rounded-lg focus:ring-1 focus:ring-[#6b4226] focus:border-transparent ${
                  errors.email ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="store@coffeebean.com"
              />
              {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Website</label>
              <input
                type="url"
                name="website"
                value={formData.website}
                onChange={handleInputChange}
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg focus:ring-1 focus:ring-[#6b4226] focus:border-transparent"
                placeholder="https://coffeebean.com"
              />
            </div>
          </div>
        </div>

        {/* Location Information */}
        <div>
          <h3 className="text-base font-semibold text-gray-700 mb-2">Location</h3>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Address <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                className={`w-full px-2.5 py-1.5 border rounded-lg focus:ring-1 focus:ring-[#6b4226] focus:border-transparent ${
                  errors.address ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="123 Main Street"
              />
              {errors.address && <p className="text-red-500 text-xs mt-1">{errors.address}</p>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  City <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className={`w-full px-2.5 py-1.5 border rounded-lg focus:ring-1 focus:ring-[#6b4226] focus:border-transparent ${
                    errors.city ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="Mumbai"
                />
                {errors.city && <p className="text-red-500 text-xs mt-1">{errors.city}</p>}
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  State <span className="text-red-500">*</span>
                </label>
                <select
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  className={`w-full px-2.5 py-1.5 border rounded-lg focus:ring-1 focus:ring-[#6b4226] focus:border-transparent ${
                    errors.state ? "border-red-500" : "border-gray-300"
                  }`}
                >
                  <option value="" disabled>
                    Select a state
                  </option>
                  {stateOptions.map((stateOption) => (
                    <option key={stateOption} value={stateOption}>
                      {stateOption}
                    </option>
                  ))}
                </select>
                {errors.state && <p className="text-red-500 text-xs mt-1">{errors.state}</p>}
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Google Maps Link
              </label>
              <input
                type="url"
                name="google_map_link"
                value={formData.google_map_link}
                onChange={handleInputChange}
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg focus:ring-1 focus:ring-[#6b4226] focus:border-transparent"
                placeholder="https://maps.google.com/..."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Latitude
                  <span className="text-gray-500 text-[10px] ml-1">(for "near me" search)</span>
                </label>
                <input
                  type="number"
                  step="any"
                  name="lat"
                  value={formData.lat}
                  onChange={handleInputChange}
                  className={`w-full px-2.5 py-1.5 border rounded-lg focus:ring-1 focus:ring-[#6b4226] focus:border-transparent ${
                    errors.lat ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="28.5494"
                />
                {errors.lat && <p className="text-red-500 text-xs mt-1">{errors.lat}</p>}
                <p className="text-[10px] text-gray-500 mt-1">
                  Get coordinates from{" "}
                  <a
                    href="https://www.google.com/maps"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    Google Maps
                  </a>
                </p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Longitude
                  <span className="text-gray-500 text-[10px] ml-1">(for "near me" search)</span>
                </label>
                <input
                  type="number"
                  step="any"
                  name="long"
                  value={formData.long}
                  onChange={handleInputChange}
                  className={`w-full px-2.5 py-1.5 border rounded-lg focus:ring-1 focus:ring-[#6b4226] focus:border-transparent ${
                    errors.long ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="77.2501"
                />
                {errors.long && <p className="text-red-500 text-xs mt-1">{errors.long}</p>}
                <p className="text-[10px] text-gray-500 mt-1">
                  Right-click on location → "What's here?"
                </p>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
              <h4 className="text-xs font-semibold text-blue-900 mb-2">
                📍 How to get Latitude & Longitude:
              </h4>
              <ol className="text-[10px] text-blue-800 space-y-1 ml-3 list-decimal">
                <li>Open <a href="https://www.google.com/maps" target="_blank" rel="noopener noreferrer" className="underline">Google Maps</a></li>
                <li>Search for your store address</li>
                <li>Right-click on the exact location</li>
                <li>Click "What's here?" or see coordinates at bottom</li>
                <li>Copy the numbers (first is Latitude, second is Longitude)</li>
              </ol>
              <p className="text-[10px] text-blue-700 mt-2">
                Example: 28.5494, 77.2501 (Delhi coordinates)
              </p>
            </div>
          </div>
        </div>

        {/* Store Description */}
        <div>
          <h3 className="text-base font-semibold text-gray-700 mb-2">Description</h3>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Store Description
            </label>
            <textarea
              name="store_description"
              value={formData.store_description}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg focus:ring-1 focus:ring-[#6b4226] focus:border-transparent"
              placeholder="Describe the store location, features, or special offerings..."
            />
          </div>
        </div>

        {/* Store Image Upload */}
        <div>
          <h3 className="text-base font-semibold text-gray-700 mb-2">Store Image</h3>
          <div className="space-y-2">
            {/* Image Preview or Placeholder */}
            <div className="flex items-center gap-3">
              <div className="relative w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg overflow-hidden bg-gray-50 flex items-center justify-center">
                {(imagePreview || (store?.image_url && !removeImage)) && (
                  <img
                    src={imagePreview || getImageUrl(store.image_url)}
                    alt="Store preview"
                    className="w-full h-full object-cover"
                  />
                )}
                {!imagePreview && !store?.image_url && !removeImage && (
                  <ImageIcon size={32} className="text-gray-400" />
                )}
                {removeImage && (
                  <div className="flex flex-col items-center justify-center text-gray-400">
                    <ImageIcon size={32} />
                    <span className="text-xs mt-1">Image removed</span>
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-2 flex-1">
                <label className="cursor-pointer">
                  <div className="px-4 py-2 bg-[#6b4226] text-white rounded-lg hover:bg-[#4a2c1a] transition-colors text-sm font-medium inline-flex items-center gap-2">
                    <Upload size={16} />
                    {selectedFile || store?.image_url ? "Change" : "Upload"}
                  </div>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleImageSelect}
                    className="hidden"
                  />
                </label>
                
                {(imagePreview || (store?.image_url && !removeImage)) && (
                  <button
                    type="button"
                    onClick={() => setShowDeleteConfirm(true)}
                    className="px-4 py-2 border border-red-500 text-red-500 rounded-lg hover:bg-red-50 transition-colors text-sm font-medium inline-flex items-center gap-2 w-fit"
                  >
                    <Trash2 size={16} />
                    Remove Image
                  </button>
                )}
                
                <p className="text-xs text-gray-500">
                  Recommended: {imageConfig.width}×{imageConfig.height}px, max {(imageConfig.maxBytes / 1024).toFixed(0)}KB
                </p>
                {imageError && (
                  <p className="text-xs text-red-500 mt-1">{imageError}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Opening Hours */}
        <div>
          <h3 className="text-base font-semibold text-gray-700 mb-2">Opening Hours</h3>
          <div className="space-y-2">
            {DAYS.map((day) => (
              <div key={day} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                <div className="w-14 font-medium text-gray-700 text-xs uppercase">{day}</div>

                <div className="flex items-center gap-2 flex-1 text-xs">
                  <label className="text-[11px] text-gray-600">Open:</label>
                  <input
                    type="time"
                    value={openingHours[day]?.open || "09:00"}
                    onChange={(e) => handleHourChange(day, "open", e.target.value)}
                    disabled={openingHours[day]?.closed}
                    className="px-1.5 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-[#6b4226] focus:border-transparent disabled:bg-gray-200 disabled:cursor-not-allowed text-xs"
                  />

                  <label className="text-[11px] text-gray-600 ml-3">Close:</label>
                  <input
                    type="time"
                    value={openingHours[day]?.close || "21:00"}
                    onChange={(e) => handleHourChange(day, "close", e.target.value)}
                    disabled={openingHours[day]?.closed}
                    className="px-1.5 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-[#6b4226] focus:border-transparent disabled:bg-gray-200 disabled:cursor-not-allowed text-xs"
                  />

                  <label className="flex items-center gap-1.5 ml-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={openingHours[day]?.closed || false}
                      onChange={() => handleClosedToggle(day)}
                      className="w-3.5 h-3.5 text-[#6b4226] border-gray-300 rounded focus:ring-[#6b4226]"
                    />
                    <span className="text-[11px] text-gray-600">Closed</span>
                  </label>
                </div>
              </div>
            ))}
          </div>
        </div>

        </div>

        {/* Form Actions - Sticky Footer */}
        <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-[#6b4226] text-white rounded-lg hover:bg-[#4a2c1a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
            disabled={isLoading}
          >
            {isLoading ? "Saving..." : store ? "Update Store" : "Create Store"}
          </button>
        </div>
      </form>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg p-6 max-w-sm mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Remove Image?</h3>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to remove this store image? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleRemoveImage}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StoreForm;
